import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Stack,
  Button,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  Icon,
  Flex,
  Image,
  Divider,
} from '@chakra-ui/react';
import { FaCheck, FaPencilAlt, FaArrowLeft, FaBrain, FaComments, FaHandPaper, FaLightbulb } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import { seoData } from '../utils/seoData';

const FeatureBox = ({ title, description, icon }) => {
  return (
    <Stack
      bg={useColorModeValue('white', 'gray.800')}
      p={6}
      rounded="xl"
      shadow="md"
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-3px)', shadow: 'lg' }}
    >
      <Flex
        w={12}
        h={12}
        align="center"
        justify="center"
        color="white"
        rounded="full"
        bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
        mb={3}
      >
        {icon}
      </Flex>
      <Heading size="md" mb={2}>
        {title}
      </Heading>
      <Text color="gray.600" fontSize="sm">
        {description}
      </Text>
    </Stack>
  );
};

const CircuitTrainingProduct = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const seo = seoData.circuitTraining[language] || seoData.circuitTraining.en;
  const isKo = language === 'ko';

  const backButtonText = isKo ? '제품 목록으로' : 'Back to Products';
  const heroTitle = isKo ? '서킷 트레이닝 프로그램' : 'Circuit Training Program';
  const heroDescription = isKo
    ? '회전식 훈련으로 구성된 체계적인 연습 프로그램으로 종합적인 문제 해결 능력을 길러 보세요.'
    : 'Structured practice program with rotating exercises designed to build comprehensive problem-solving skills through systematic training.';
  const heroButton = isKo ? '도입 상담하기' : 'Get Started';

  const featureCards = isKo
    ? [
        {
          title: '회전형 연습 스테이션',
          description: '다양한 문제 유형을 순차적으로 풀면서 전 영역의 실력을 고르게 키울 수 있습니다.',
          icon: <Icon as={FaPencilAlt} w={6} h={6} />,
        },
        {
          title: '난이도 단계별 상승',
          description: '기초 개념부터 고난도 문제까지 단계적으로 학습해 자연스럽게 실력을 향상하세요.',
          icon: <Icon as={FaPencilAlt} w={6} h={6} />,
        },
        {
          title: '시간 기반 세션',
          description: '실제 시험 환경과 유사한 시간 제한 훈련으로 속도와 정확도를 동시에 향상합니다.',
          icon: <Icon as={FaPencilAlt} w={6} h={6} />,
        },
        {
          title: '맞춤 학습 경로',
          description: 'AI가 학습 성과와 스타일을 분석해 최적의 학습 경로를 추천합니다.',
          icon: <Icon as={FaPencilAlt} w={6} h={6} />,
        },
      ]
    : [
        {
          title: 'Rotating Practice Stations',
          description:
            'Move through different problem types systematically, ensuring comprehensive skill development across all areas.',
          icon: <Icon as={FaPencilAlt} w={6} h={6} />,
        },
        {
          title: 'Progressive Difficulty',
          description:
            'Start with foundational concepts and gradually advance to more challenging problems as your skills improve.',
          icon: <Icon as={FaPencilAlt} w={6} h={6} />,
        },
        {
          title: 'Time-Based Sessions',
          description: 'Build speed and accuracy with timed circuit sessions that simulate real test conditions.',
          icon: <Icon as={FaPencilAlt} w={6} h={6} />,
        },
        {
          title: 'Personalized Paths',
          description: 'AI-driven recommendations adapt your training path based on your performance and learning style.',
          icon: <Icon as={FaPencilAlt} w={6} h={6} />,
        },
      ];

  const benefits = isKo
    ? [
        '모든 문제 유형을 아우르는 균형 잡힌 실력 향상',
        '문제 해결 속도와 정확도 향상',
        '간격 반복을 통한 학습 유지력 강화',
        '시험 상황에서도 흔들리지 않는 자신감 제공',
        '세부적인 성과 분석 리포트 제공',
        '훈련 시간과 난이도를 자유롭게 조정',
        '성과 추적 및 마일스톤 관리',
        '데스크톱과 모바일에서 모두 이용 가능',
      ]
    : [
        'Comprehensive skill coverage across all problem types',
        'Improved problem-solving speed and accuracy',
        'Better retention through spaced repetition',
        'Enhanced test-taking confidence',
        'Detailed performance analytics',
        'Customizable circuit duration and difficulty',
        'Progress tracking and milestone achievements',
        'Mobile and desktop access',
      ];

  const customSteps = isKo
    ? [
        {
          number: '1',
          title: '학습 집중 영역 선택',
          description:
            '체계적으로 정리된 과목과 단원을 살펴보며 연습이 필요한 영역을 빠르게 찾을 수 있습니다.',
          bullets: [
            '여러 과목 중에서 선택 가능',
            '각 과목별 세부 단원 선택',
            '단원별 문제 수를 한눈에 확인',
          ],
          imageAlt: '과목과 단원 선택',
        },
        {
          number: '2',
          title: '문항 유형 맞춤 설정',
          description:
            '문항 유형, 난이도, 형식을 필터링하여 나만의 문제 세트를 만들고 진행 상황을 실시간으로 추적하세요.',
          bullets: [
            '문항 유형과 난이도를 세밀하게 필터링',
            '완료 상태를 한눈에 확인',
            '진행률과 성취도 추적',
          ],
          imageAlt: '문항 유형 맞춤 설정',
        },
      ]
    : [
        {
          number: '1',
          title: 'Select Your Focus Areas',
          description:
            'Browse through organized subjects and units to find exactly what you need to practice. Our comprehensive library covers all major topics with clear categorization.',
          bullets: [
            'Choose from multiple subjects',
            'Select specific units within each subject',
            'View problem counts for each topic',
          ],
          imageAlt: 'Choose Units and Topics',
        },
        {
          number: '2',
          title: 'Customize Problem Types',
          description:
            'Filter problems by type, difficulty, and format to create the perfect practice session. Track your progress and completion status for each problem set.',
          bullets: [
            'Filter by problem type and difficulty',
            'See completion status at a glance',
            'Track your progress percentage',
          ],
          imageAlt: 'Choose Problem Types',
        },
      ];

  const aiAnalysis = isKo
    ? {
        sectionTitle: 'AI 기반 해설 리뷰',
        sectionDescription:
          '문제 풀이가 끝나면 AI가 자동으로 상세 해설과 맞춤형 피드백을 제공하여 학습 효과를 극대화합니다.',
        intelligentTitle: '지능형 해설 분석',
        intelligentDescription:
          'AI가 오답 패턴을 파악하고 단계별 해설을 제공해 스스로 학습을 이어갈 수 있도록 돕습니다.',
        intelligentBullets: [
          {
            title: 'AI 분석',
            description: '취약한 문제 유형과 반복되는 실수를 정확하게 짚어줍니다.',
          },
          {
            title: 'AI 상담',
            description: '추가 질문을 하면 바로 답변을 제공해 개념을 깊이 이해할 수 있습니다.',
          },
        ],
        handwritingTitle: '손글씨 풀이 분석',
        handwritingDescription:
          '손으로 쓴 풀이 과정도 사진으로 업로드하면 AI가 오류 지점을 찾아 개선 방향을 제시합니다.',
        handwritingBullets: [
          {
            title: '풀이 분석',
            description: '손글씨 풀이를 단계별로 분석해 실수한 부분을 명확하게 알려줍니다.',
          },
          {
            title: '개선 제안',
            description: '약점을 보완할 수 있는 맞춤형 학습 전략을 추천합니다.',
          },
        ],
        highlightTitle: 'AI가 제안하는 맞춤 학습',
        highlightDescription:
          'AI는 정답을 알려주는 것을 넘어 학습자의 여정을 이해하고 필요한 도움을 정확하게 제공합니다.',
      }
    : {
        sectionTitle: 'AI-Powered Solution Review',
        sectionDescription:
          'After solving problem sets, students receive comprehensive AI-generated solutions and personalized feedback to accelerate their learning.',
        intelligentTitle: 'Intelligent Solution Analysis',
        intelligentDescription:
          'Get instant access to detailed AI-generated solutions with step-by-step explanations. Our AI identifies patterns in your mistakes and provides personalized insights.',
        intelligentBullets: [
          {
            title: 'AI Analytics',
            description: 'Identifies problem types you struggle with and patterns in your errors',
          },
          {
            title: 'Chat with AI',
            description: 'Ask follow-up questions and get instant clarification on concepts',
          },
        ],
        handwritingTitle: 'Handwritten Work Analysis',
        handwritingDescription:
          'Upload photos of your handwritten work and let our AI analyze your problem-solving process. Get pinpointed feedback on exactly where you went wrong.',
        handwritingBullets: [
          {
            title: 'Work Analysis',
            description: 'AI examines your handwritten solutions to identify specific errors',
          },
          {
            title: 'Improvement Suggestions',
            description: 'Receive targeted recommendations to strengthen weak areas',
          },
        ],
        highlightTitle: 'Personalized Learning with AI',
        highlightDescription:
          "Our AI doesn't just show you the right answer—it understands your unique learning journey, pinpoints exactly where you need help, and adapts to your individual needs.",
      };

  const highlightTitle = isKo ? '지금 바로 훈련을 시작하세요' : 'Start Training Today';
  const customizedTitle = isKo ? '맞춤형 학습 경로' : 'Customized Learning Paths';
  const customizedDescription = isKo
    ? '학생들은 학습 목표에 맞춰 과목, 단원, 문제 유형을 직접 선택하여 자신만의 문제 세트를 만들 수 있습니다.'
    : 'Students can choose specific subjects, units, and problem types to create personalized problem sets that match their learning goals.';

  const cta = {
    title: isKo ? '지금 학습 혁신을 시작해 보세요' : 'Ready to Transform Your Learning?',
    description: isKo
      ? '수많은 학생들이 서킷 트레이닝 프로그램으로 문제 해결 실력을 빠르게 향상하고 있습니다.'
      : 'Join thousands of students improving their problem-solving skills with our Circuit Training Program.',
    primary: isKo ? '영업팀에 문의하기' : 'Contact Sales',
    secondary: isKo ? '전체 제품 보기' : 'View All Products',
  };

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical="/products/circuit-training"
        lang={language}
        ogImage="/screenshots/circuit-training.png"
      />
      <Box>
        {/* Back Button */}
      <Box bg={useColorModeValue('white', 'gray.800')} py={4} borderBottom="1px" borderColor={useColorModeValue('gray.200', 'gray.700')}>
        <Container maxW="container.xl">
          <Button
            leftIcon={<FaArrowLeft />}
            variant="ghost"
            onClick={() => navigate('/products')}
            _hover={{ color: '#FF6A5F' }}
          >
            {backButtonText}
          </Button>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        bgGradient="linear(to-br, #FF6A5F, #A63FF5, #C56CFF)"
        color="white"
        py={20}
      >
        <Container maxW="container.xl">
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            align="center"
            justify="space-between"
            gap={10}
          >
            <VStack align={{ base: 'center', lg: 'flex-start' }} spacing={6} flex={1}>
              <Flex
                w={20}
                h={20}
                align="center"
                justify="center"
                color="white"
                rounded="full"
                bg="whiteAlpha.200"
              >
                <Icon as={FaPencilAlt} w={10} h={10} />
              </Flex>
              <Heading
                size="3xl"
                fontWeight="extrabold"
                textAlign={{ base: 'center', lg: 'left' }}
              >
                {heroTitle}
              </Heading>
              <Text
                fontSize="xl"
                textAlign={{ base: 'center', lg: 'left' }}
                maxW="600px"
              >
                {heroDescription}
              </Text>
              <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
                <Button
                  size="lg"
                  bg="white"
                  color="#A63FF5"
                  px={8}
                  _hover={{
                    bg: 'gray.100',
                    transform: 'translateY(-2px)',
                    shadow: 'xl',
                  }}
                  transition="all 0.3s"
                  onClick={() => navigate('/contact')}
                >
                  {heroButton}
                </Button>
              </Stack>
            </VStack>
            <Box flex={1} display={{ base: 'none', lg: 'block' }}>
              <Image
                src="/screenshots/circuit-training.png"
                alt="Circuit Training Program"
                rounded="2xl"
                shadow="2xl"
                w="full"
                h="auto"
                objectFit="contain"
              />
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl">{isKo ? '핵심 기능' : 'Key Features'}</Heading>
              <Text fontSize="lg" color="gray.600" maxW="600px">
                {isKo
                  ? '체계적인 훈련을 통해 문제 해결 능력을 완성할 수 있는 모든 기능을 제공합니다.'
                  : 'Everything you need to master problem-solving through systematic practice'}
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full">
              {featureCards.map((feature, index) => (
                <FeatureBox key={index} {...feature} />
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <Divider />

      {/* Benefits Section */}
      <Box py={20} bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            <VStack align="flex-start" spacing={6}>
              <Heading size="2xl">{isKo ? '서킷 트레이닝을 선택해야 하는 이유' : 'Why Choose Circuit Training?'}</Heading>
              <Text fontSize="lg" color="gray.600">
                {isKo
                  ? '검증된 교육 연구를 바탕으로 설계된 서킷 트레이닝은 학습 효율과 기억 지속력을 극대화합니다.'
                  : 'Our circuit training methodology is based on proven educational research and designed to maximize learning efficiency and retention.'}
              </Text>
              <List spacing={3}>
                {benefits.map((benefit, index) => (
                  <ListItem key={index} display="flex" alignItems="center">
                    <ListIcon as={FaCheck} color="green.500" fontSize="lg" />
                    <Text>{benefit}</Text>
                  </ListItem>
                ))}
              </List>
            </VStack>

            <Box
              bgGradient="linear(135deg, #FF6A5F, #A63FF5, #C56CFF, #FF8FA5)"
              h="400px"
              rounded="2xl"
              display="flex"
              alignItems="center"
              justifyContent="center"
              shadow="2xl"
            >
              <VStack spacing={4} color="white">
                <Icon as={FaPencilAlt} w={20} h={20} />
                <Heading size="xl">{highlightTitle}</Heading>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <Divider />

      {/* How It Works Section */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl">{customizedTitle}</Heading>
              <Text fontSize="lg" color="gray.600" maxW="800px">
                {customizedDescription}
              </Text>
            </VStack>

            {/* Units Selection */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} w="full" alignItems="center">
              <Box order={{ base: 2, lg: 1 }}>
                <Image
                  src="/screenshots/circuit-training-units-page.png"
                  alt={customSteps[0].imageAlt}
                  rounded="2xl"
                  shadow="2xl"
                  w="full"
                  h="auto"
                  objectFit="cover"
                  border="1px"
                  borderColor={useColorModeValue('gray.200', 'gray.700')}
                />
              </Box>
              <VStack align="flex-start" spacing={4} order={{ base: 1, lg: 2 }}>
                <Flex
                  w={14}
                  h={14}
                  align="center"
                  justify="center"
                  color="white"
                  rounded="full"
                  bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
                >
                  <Text fontSize="2xl" fontWeight="bold">{customSteps[0].number}</Text>
                </Flex>
                <Heading size="xl">{customSteps[0].title}</Heading>
                <Text fontSize="lg" color="gray.600">
                  {customSteps[0].description}
                </Text>
                <List spacing={3}>
                  {customSteps[0].bullets.map((item, idx) => (
                    <ListItem key={idx} display="flex" alignItems="center">
                      <ListIcon as={FaCheck} color="green.500" fontSize="lg" />
                      <Text>{item}</Text>
                    </ListItem>
                  ))}
                </List>
              </VStack>
            </SimpleGrid>

            {/* Problems Selection */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} w="full" alignItems="center">
              <VStack align="flex-start" spacing={4}>
                <Flex
                  w={14}
                  h={14}
                  align="center"
                  justify="center"
                  color="white"
                  rounded="full"
                  bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
                >
                  <Text fontSize="2xl" fontWeight="bold">{customSteps[1].number}</Text>
                </Flex>
                <Heading size="xl">{customSteps[1].title}</Heading>
                <Text fontSize="lg" color="gray.600">
                  {customSteps[1].description}
                </Text>
                <List spacing={3}>
                  {customSteps[1].bullets.map((item, idx) => (
                    <ListItem key={idx} display="flex" alignItems="center">
                      <ListIcon as={FaCheck} color="green.500" fontSize="lg" />
                      <Text>{item}</Text>
                    </ListItem>
                  ))}
                </List>
              </VStack>
              <Box>
                <Image
                  src="/screenshots/circuit-training-problem-page.png"
                  alt={customSteps[1].imageAlt}
                  rounded="2xl"
                  shadow="2xl"
                  w="full"
                  h="auto"
                  objectFit="cover"
                  border="1px"
                  borderColor={useColorModeValue('gray.200', 'gray.700')}
                />
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <Divider />

      {/* AI-Powered Review Section */}
      <Box py={20} bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl">{aiAnalysis.sectionTitle}</Heading>
              <Text fontSize="lg" color="gray.600" maxW="800px">
                {aiAnalysis.sectionDescription}
              </Text>
            </VStack>

            {/* AI Analytics and Chat */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} w="full" alignItems="center">
              <Box order={{ base: 2, lg: 1 }}>
                <Image
                  src="/screenshots/circuit-training-answer-page.png"
                  alt="AI-Generated Solutions"
                  rounded="2xl"
                  shadow="2xl"
                  w="full"
                  h="auto"
                  objectFit="cover"
                  border="1px"
                  borderColor={useColorModeValue('gray.200', 'gray.700')}
                />
              </Box>
              <VStack align="flex-start" spacing={4} order={{ base: 1, lg: 2 }}>
                <Flex
                  w={14}
                  h={14}
                  align="center"
                  justify="center"
                  color="white"
                  rounded="full"
                  bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
                >
                  <Text fontSize="2xl" fontWeight="bold">3</Text>
                </Flex>
                <Heading size="xl">{aiAnalysis.intelligentTitle}</Heading>
                <Text fontSize="lg" color="gray.600">
                  {aiAnalysis.intelligentDescription}
                </Text>
                <List spacing={3}>
                  {aiAnalysis.intelligentBullets.map((item, idx) => (
                    <ListItem key={idx} display="flex" alignItems="flex-start">
                      <ListIcon
                        as={idx === 0 ? FaBrain : FaComments}
                        color="#A63FF5"
                        fontSize="lg"
                        mt={1}
                      />
                      <Text>
                        <strong>{item.title}:</strong> {item.description}
                      </Text>
                    </ListItem>
                  ))}
                </List>
              </VStack>
            </SimpleGrid>

            {/* Handwritten Work Analysis */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} w="full" alignItems="center">
              <VStack align="flex-start" spacing={4}>
                <Flex
                  w={14}
                  h={14}
                  align="center"
                  justify="center"
                  color="white"
                  rounded="full"
                  bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
                >
                  <Text fontSize="2xl" fontWeight="bold">4</Text>
                </Flex>
                <Heading size="xl">{aiAnalysis.handwritingTitle}</Heading>
                <Text fontSize="lg" color="gray.600">
                  {aiAnalysis.handwritingDescription}
                </Text>
                <List spacing={3}>
                  {aiAnalysis.handwritingBullets.map((item, idx) => (
                    <ListItem key={idx} display="flex" alignItems="flex-start">
                      <ListIcon
                        as={idx === 0 ? FaHandPaper : FaLightbulb}
                        color="#A63FF5"
                        fontSize="lg"
                        mt={1}
                      />
                      <Text>
                        <strong>{item.title}:</strong> {item.description}
                      </Text>
                    </ListItem>
                  ))}
                </List>
              </VStack>
              <Box>
                <Image
                  src="/screenshots/circuit-training-answer-page-2.png"
                  alt="Handwritten Work Analysis"
                  rounded="2xl"
                  shadow="2xl"
                  w="full"
                  h="auto"
                  objectFit="cover"
                  border="1px"
                  borderColor={useColorModeValue('gray.200', 'gray.700')}
                />
              </Box>
            </SimpleGrid>

            {/* Key AI Features Highlight */}
            <Box
              w="full"
              bgGradient="linear(135deg, #FF6A5F, #A63FF5, #C56CFF, #FF8FA5)"
              p={10}
              rounded="2xl"
              shadow="2xl"
            >
              <VStack spacing={6} color="white">
                <Icon as={FaBrain} w={16} h={16} />
                <Heading size="xl" textAlign="center">
                  {aiAnalysis.highlightTitle}
                </Heading>
                <Text fontSize="lg" textAlign="center" maxW="700px">
                  {aiAnalysis.highlightDescription}
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      <Divider />

      {/* CTA Section */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack
            spacing={6}
            bg={useColorModeValue('white', 'gray.800')}
            p={12}
            rounded="2xl"
            shadow="xl"
            textAlign="center"
          >
            <Heading size="2xl">{cta.title}</Heading>
            <Text fontSize="lg" color="gray.600" maxW="600px">
              {cta.description}
            </Text>
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
              <Button
                size="lg"
                bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
                color="white"
                px={8}
                _hover={{
                  bgGradient: "linear(to-r, #FF8FA5, #C56CFF)",
                  transform: 'translateY(-2px)',
                  shadow: 'xl',
                }}
                transition="all 0.3s"
                onClick={() => navigate('/contact')}
              >
                {cta.primary}
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="#A63FF5"
                color="#A63FF5"
                px={8}
                _hover={{
                  bg: '#A63FF5',
                  color: 'white',
                }}
                transition="all 0.3s"
                onClick={() => navigate('/products')}
              >
                {cta.secondary}
              </Button>
            </Stack>
          </VStack>
        </Container>
      </Box>
      </Box>
    </>
  );
};

export default CircuitTrainingProduct;

